/**
 * Bitmap-font measurement against the baked `.FNT` atlases. Widths come from
 * the bake manifest's per-glyph metrics (`FontMeta.glyphs`), which are the
 * font file's own advance widths — no estimation, no CSS text measurement.
 * Pure, so centering can be asserted against the pixel positions measured off
 * the original screenshots.
 */

import type { BakeManifest, FontMeta, GlyphMeta } from "@red-opus/baker/schema";
import type { FontId } from "./draw-ops.ts";

export interface FontAtlas {
  readonly id: FontId;
  readonly meta: FontMeta;
  readonly glyphs: ReadonlyMap<number, GlyphMeta>;
}

export const FONT_ASSET_IDS: Readonly<Record<FontId, string>> = {
  FONT1: "fonts/FONT1.FNT",
  FONT2: "fonts/FONT2.FNT",
  FONT3: "fonts/FONT3.FNT",
  TALK: "fonts/TALK.FNT",
};

export const SHELL_FONTS: readonly FontId[] = ["FONT1", "FONT2", "FONT3", "TALK"];

export function fontAtlasPath(font: FontId): string {
  return `/baked/${FONT_ASSET_IDS[font]}.png`;
}

function isFontMeta(meta: unknown): meta is FontMeta {
  return typeof meta === "object" && meta !== null && Array.isArray((meta as { glyphs?: unknown }).glyphs);
}

export function readFontMeta(manifest: BakeManifest, font: FontId): FontMeta {
  const entry = manifest.entries.find((candidate) => candidate.id === FONT_ASSET_IDS[font]);
  if (!entry || !isFontMeta(entry.meta)) {
    throw new Error(`shell: bake manifest has no font metrics for ${FONT_ASSET_IDS[font]} — rebake data/baked`);
  }
  return entry.meta;
}

export function buildFontAtlas(id: FontId, meta: FontMeta): FontAtlas {
  return { id, meta, glyphs: new Map(meta.glyphs.map((glyph) => [glyph.code, glyph])) };
}

/** Missing codepoints fall back to the space glyph, matching the original's tolerance for stray characters. */
export function glyphFor(atlas: FontAtlas, code: number): GlyphMeta | undefined {
  return atlas.glyphs.get(code) ?? atlas.glyphs.get(0x20);
}

export function measureText(atlas: FontAtlas, text: string): number {
  let width = 0;
  for (const character of text) {
    width += glyphFor(atlas, character.codePointAt(0) ?? 0x20)?.width ?? 0;
  }
  return width;
}

/**
 * Greedy wrap to a pixel width, so prose fits the box it is drawn in rather than
 * a guessed character count. Lives here rather than in the model layer because
 * it is pure font arithmetic and the *renderer* needs it too: a popup's body is
 * wrapped at layout time, which is the only place in that path that holds an
 * atlas (`popup-chrome.ts`).
 *
 * A single word wider than the column is emitted whole rather than cut — the
 * original never has one, and a silently truncated word would be worse than a
 * visibly wide box.
 */
export function wrapText(atlas: FontAtlas, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.split(/\s+/).filter((part) => part !== "")) {
    const candidate = line === "" ? word : `${line} ${word}`;
    if (measureText(atlas, candidate) <= maxWidth) line = candidate;
    else {
      if (line !== "") lines.push(line);
      line = word;
    }
  }
  if (line !== "") lines.push(line);
  return lines;
}

/**
 * Left edge for a string centred on `centerX`. `ceil` rather than `round`
 * reproduces the original's own rounding: every one of the seven Main Menu
 * labels lands on the exact x measured from `main-menu.png` with this rule
 * (see `shell-text-metrics.test.ts`).
 */
export function centerTextX(atlas: FontAtlas, text: string, centerX: number): number {
  return centerX - Math.ceil(measureText(atlas, text) / 2);
}
